export const MAX_SECTION_CHARS = 50_000;

const BANNED_URI_PROTOCOLS = /^(javascript|vbscript):/i;
const BANNED_DATA_URI = /^data:text\/html/i;

/**
 * Clone a section element, sanitise it, and serialise to HTML.
 * Throws if the serialised result exceeds MAX_SECTION_CHARS.
 * Used in v0.1.0 for section-click capture; retained for tests and potential future re-use.
 */
export function captureSection(element) {
  const cloned = element.cloneNode(true);
  sanitise(cloned);
  const html = serialise(cloned);
  if (html.length > MAX_SECTION_CHARS) {
    throw new Error(`Captured section is too large (${html.length} chars > ${MAX_SECTION_CHARS}). Try capturing a sub-section.`);
  }
  return html;
}

/**
 * v0.2.0: capture whatever's currently selected (highlighted) inside the iframe.
 * iframeWindow must be the iframe's window (typically `iframeEl.contentWindow`).
 * Returns sanitised HTML of the selected range.
 * Throws if no selection or if size exceeds MAX_SECTION_CHARS.
 */
export function captureSelection(iframeWindow) {
  const selection = iframeWindow.getSelection?.();
  if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
    throw new Error('Nothing selected. Highlight some content in the preview first, then click Capture.');
  }
  const range = selection.getRangeAt(0);
  if (range.collapsed) {
    throw new Error('Nothing selected. Highlight some content in the preview first, then click Capture.');
  }
  const doc = iframeWindow.document;
  const fragment = range.cloneContents();
  const wrapper = doc.createElement('div');
  wrapper.appendChild(fragment);
  sanitise(wrapper);
  const html = wrapper.innerHTML;
  if (html.length > MAX_SECTION_CHARS) {
    throw new Error(`Selection too large (${html.length} chars > ${MAX_SECTION_CHARS}). Highlight a smaller range.`);
  }
  return html;
}

/**
 * Find the heading (h1/h2/h3) that best labels a selection starting at startEl.
 * Three resolution rules, in order:
 *   1. If startEl IS a heading, return it.
 *   2. If startEl is the same as or follows (in document order) some heading,
 *      return the LATEST such heading — that's the section the selection sits in.
 *   3. If startEl is an ancestor of some heading (i.e., the user selected a
 *      whole section element from the outside, and the heading is a descendant),
 *      return the FIRST such heading inside startEl.
 * Falls back to 'Selected content' if no heading exists.
 *
 * Uses compareDocumentPosition for correctness across nested structures.
 */
export function findEnclosingSectionTitle(startEl) {
  if (!startEl) return 'Selected content';
  const doc = startEl.ownerDocument;
  if (!doc) return 'Selected content';

  const headings = Array.from(doc.querySelectorAll('h1, h2, h3'));
  if (headings.length === 0) return 'Selected content';

  const FOLLOWING = 4; // Node.DOCUMENT_POSITION_FOLLOWING

  // Walk backward: the LAST heading that precedes startEl (or is startEl) wins.
  // If startEl is an ancestor of a heading, fall through to that case.
  for (let i = headings.length - 1; i >= 0; i--) {
    const h = headings[i];
    if (h === startEl) return h.textContent.trim() || 'Selected content';

    const pos = h.compareDocumentPosition(startEl);
    if (pos & FOLLOWING) {
      return h.textContent.trim() || 'Selected content';
    }

    if (startEl.contains?.(h)) {
      // startEl is an ancestor section containing this heading.
      // Use the FIRST heading inside startEl as the section's own title.
      const firstInside = startEl.querySelector('h1, h2, h3');
      if (firstInside) return firstInside.textContent.trim() || 'Selected content';
    }
  }

  return 'Selected content';
}

function sanitise(node) {
  if (node.nodeType !== 1) return;
  const tag = node.tagName?.toLowerCase();

  // Drop scripts entirely.
  if (tag === 'script' || tag === 'style' && node.textContent.includes('@import')) {
    node.remove();
    return;
  }

  // Strip event handlers and dangerous URIs.
  const toRemove = [];
  for (const attr of Array.from(node.attributes || [])) {
    const name = attr.name.toLowerCase();
    const value = attr.value;
    if (name.startsWith('on')) toRemove.push(name);
    else if ((name === 'href' || name === 'src') && BANNED_URI_PROTOCOLS.test(value)) toRemove.push(name);
    else if ((name === 'href' || name === 'src') && BANNED_DATA_URI.test(value)) toRemove.push(name);
  }
  for (const n of toRemove) node.removeAttribute(n);

  // Recurse into children. Iterate over a snapshot because we may mutate.
  for (const child of Array.from(node.children)) sanitise(child);
}

function serialise(node) {
  // Use outerHTML if it's an Element; otherwise build a string.
  if ('outerHTML' in node) return node.outerHTML;
  // Fallback (shouldn't hit in practice).
  const wrapper = node.ownerDocument.createElement('div');
  wrapper.appendChild(node);
  return wrapper.innerHTML;
}
